import * as THREE from 'three';
import { WATER, TERRAIN } from '../config';
import { terrainHeight } from './terrain';

const UP = new THREE.Vector3(0, 1, 0);

// Underwater life: fish schools + swaying kelp + coral clusters, spawned ONLY where the seabed is
// underwater. A follow-grid of WATER.lifeGrid^2 cells (WATER.lifeCell wide) recenters on the focus
// like grass.ts. Each cell's layout is deterministic (mulberry32 seeded by cell coords) so
// recentering never reshuffles. Fish swim + kelp sways in the VERTEX SHADER off a per-instance seed
// + iTime (no per-frame CPU matrix work); coral is rigid (the moving water + caustics sell it).
// Three InstancedMeshes total (fish, kelp, coral), each holding every cell's instances; cells far
// from the focus (or over dry seabed) collapse their instances to a zero matrix so they draw
// nothing. Cosmetic, client-only.

const CELL = WATER.lifeCell;
const GRID = WATER.lifeGrid;
const CELLS = GRID * GRID;
const FISH = WATER.fishPerCell;
const KELP = WATER.kelpPerCell;
const CORAL = WATER.coralPerCell;
// Underwater test: only populate a cell whose seabed sits at least minDepth below the surface.
const DEEP_Y = TERRAIN.seaLevel - WATER.minDepth;
// Per-instance gate: a cell can straddle the shoreline, so each fish/kelp/coral is also dropped if
// the seabed UNDER IT is not underwater (must have at least this much water over it). Keeps life off
// the dry grass that rises inside a populated cell.
const SHORE_Y = TERRAIN.seaLevel - 0.5;
// Fish swim between the seabed and ~1m below the surface.
const FISH_CEIL = TERRAIN.seaLevel - 1.0;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function cellSeed(cx: number, cz: number): number {
  let h = (Math.imul(cx, 374761393) + Math.imul(cz, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

// One follow-grid slot: a fixed range of instance indices in each mesh, owning one world cell.
interface Cell {
  fishBase: number;  // first fish instance index for this slot
  kelpBase: number;  // first kelp instance index for this slot
  coralBase: number; // first coral instance index for this slot
  cellX: number;
  cellZ: number;
  populated: boolean; // seabed underwater here -> instances were laid out, else empty
  culled: boolean;    // center past lifeCullRadius -> instances collapsed to zero (drawn as nothing)
}

// A small stretched fish body (octahedron) + a flat tail fin, centered at origin, pointing +Z.
// The tail verts are tagged (via the alpha channel of a vertex color) so the shader can wiggle
// only the tail. Two muted fish tones baked per-vertex would clash with the per-instance tint, so
// color comes from instanceColor; geometry carries only a tail weight in a 'tail' attribute.
function makeFishGeometry(): THREE.BufferGeometry {
  const body = new THREE.OctahedronGeometry(1, 0);
  // Stretch into a slim fish: long in Z (forward), short in Y, slim in X.
  body.scale(0.16, 0.2, 0.42);
  const tail = new THREE.ConeGeometry(0.18, 0.26, 4).rotateX(-Math.PI / 2);
  tail.translate(0, 0, -0.5); // sits behind the body, fanning back
  // Merge by hand (no addon needed): concat position + a per-vertex tail weight.
  const bp = body.attributes.position as THREE.BufferAttribute;
  const tp = tail.attributes.position as THREE.BufferAttribute;
  const total = bp.count + tp.count;
  const pos = new Float32Array(total * 3);
  const tw = new Float32Array(total); // 1 on tail verts, 0 on body
  pos.set(bp.array as Float32Array, 0);
  pos.set(tp.array as Float32Array, bp.count * 3);
  for (let i = bp.count; i < total; i++) tw[i] = 1;
  const idx: number[] = [];
  const bi = body.getIndex();
  const ti = tail.getIndex();
  if (bi) for (let i = 0; i < bi.count; i++) idx.push(bi.getX(i));
  else for (let i = 0; i < bp.count; i++) idx.push(i);
  const off = bp.count;
  if (ti) for (let i = 0; i < ti.count; i++) idx.push(ti.getX(i) + off);
  else for (let i = 0; i < tp.count; i++) idx.push(i + off);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('tail', new THREE.BufferAttribute(tw, 1));
  geom.setIndex(idx);
  geom.computeVertexNormals();
  body.dispose();
  tail.dispose();
  return geom;
}

// A tall thin kelp blade: a stack of segments so the vertex shader can bend it into a curve, with
// the base at y=0 (rooted on the seabed) and the top at y=1 (scaled to reach toward the surface).
// Carries a 'sway' weight (0 at root, 1 at tip) so the tip moves most, like grass wind.
function makeKelpGeometry(): THREE.BufferGeometry {
  const segs = 5;
  const halfW = 0.06; // blade half-width at the base
  const vcount = (segs + 1) * 2;
  const pos = new Float32Array(vcount * 3);
  const sway = new Float32Array(vcount);
  for (let s = 0; s <= segs; s++) {
    const t = s / segs; // 0 root .. 1 tip
    const w = halfW * (1 - t * 0.7); // taper toward the tip
    const y = t; // unit-tall; instance scale stretches base->surface
    const wgt = t * t; // tip sways most (quadratic), root pinned
    const i0 = s * 2, i1 = s * 2 + 1;
    pos[i0 * 3] = -w; pos[i0 * 3 + 1] = y; pos[i0 * 3 + 2] = 0;
    pos[i1 * 3] = w; pos[i1 * 3 + 1] = y; pos[i1 * 3 + 2] = 0;
    sway[i0] = wgt; sway[i1] = wgt;
  }
  const idx: number[] = [];
  for (let s = 0; s < segs; s++) {
    const a = s * 2, b = s * 2 + 1, c = (s + 1) * 2, d = (s + 1) * 2 + 1;
    idx.push(a, c, b, b, c, d);
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('sway', new THREE.BufferAttribute(sway, 1));
  geom.setIndex(idx);
  geom.computeVertexNormals();
  return geom;
}

// Merge several position+index geometries into one (no addon needed, matching makeFishGeometry's
// hand-merge). Re-computes normals at the end and disposes the parts.
function mergeGeoms(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vtotal = 0;
  for (const g of parts) vtotal += (g.attributes.position as THREE.BufferAttribute).count;
  const pos = new Float32Array(vtotal * 3);
  const idx: number[] = [];
  let voff = 0;
  for (const g of parts) {
    const gp = g.attributes.position as THREE.BufferAttribute;
    pos.set(gp.array as Float32Array, voff * 3);
    const gi = g.getIndex();
    if (gi) for (let i = 0; i < gi.count; i++) idx.push(gi.getX(i) + voff);
    else for (let i = 0; i < gp.count; i++) idx.push(i + voff);
    voff += gp.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setIndex(idx);
  out.computeVertexNormals();
  for (const g of parts) g.dispose();
  return out;
}

// A small branching coral: a stubby trunk plus a few tapered branches angling up and out, all rooted
// at y=0 so the instance seats on the seabed. Unit-ish scale; the instance matrix sizes each cluster.
function makeCoralGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(new THREE.CylinderGeometry(0.05, 0.1, 0.5, 6).translate(0, 0.25, 0)); // trunk
  const N = 5;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const len = 0.32 + (i % 2) * 0.2;
    const br = new THREE.CylinderGeometry(0.018, 0.05, len, 5);
    br.translate(0, len * 0.5, 0);          // base at origin, tip at +Y
    br.rotateZ(0.5 + (i % 3) * 0.15);        // tilt outward
    br.rotateY(a);                           // spread around the trunk
    br.translate(Math.cos(a) * 0.06, 0.28 + (i % 2) * 0.12, Math.sin(a) * 0.06); // attach up the trunk
    parts.push(br);
  }
  return mergeGeoms(parts);
}

export class UnderwaterLife {
  readonly group = new THREE.Group();
  private readonly uTime: { value: number };
  private readonly fish: THREE.InstancedMesh;
  private readonly kelp: THREE.InstancedMesh;
  private readonly coral: THREE.InstancedMesh;
  // Per-instance swim/sway data baked into instanced attributes (animated in the shaders).
  private readonly fishSeed: Float32Array;   // vec4: random phase/speed/radius/bob per fish
  private readonly fishOrigin: Float32Array; // vec3: cell-local rest position (seabed-relative)
  private readonly kelpSeed: Float32Array;   // vec2: phase + per-blade sway scale
  // Cached live transforms (16 floats each): cull swaps the instanceMatrix between these and zero,
  // so re-entering a cell never re-runs terrainHeight. Sized to one transform per instance.
  private readonly fishLive: Float32Array;
  private readonly kelpLive: Float32Array;
  private readonly coralLive: Float32Array;
  private readonly cells: Cell[] = [];
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();
  private readonly tmpCol = new THREE.Color();
  private curX = NaN;
  private curZ = NaN;

  constructor(uTime: { value: number }) {
    this.uTime = uTime;
    const fishCount = CELLS * FISH;
    const kelpCount = CELLS * KELP;
    const coralCount = CELLS * CORAL;
    this.fishLive = new Float32Array(fishCount * 16);
    this.kelpLive = new Float32Array(kelpCount * 16);
    this.coralLive = new Float32Array(coralCount * 16);

    // --- FISH --- (instanceColor tints each fish; no vertexColors, which would need a per-vertex
    // 'color' attribute we do not have and would render the fish black).
    const fishMat = new THREE.MeshLambertMaterial({});
    this.fishSeed = new Float32Array(fishCount * 4);
    this.fishOrigin = new Float32Array(fishCount * 3);
    const fishSeedAttr = new THREE.InstancedBufferAttribute(this.fishSeed, 4);
    const fishOriginAttr = new THREE.InstancedBufferAttribute(this.fishOrigin, 3);
    fishSeedAttr.setUsage(THREE.DynamicDrawUsage);
    fishOriginAttr.setUsage(THREE.DynamicDrawUsage);
    const fishGeom = makeFishGeometry();
    fishGeom.setAttribute('fishSeed', fishSeedAttr);
    fishGeom.setAttribute('fishOrigin', fishOriginAttr);
    this.fish = new THREE.InstancedMesh(fishGeom, fishMat, fishCount);
    this.fish.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.fish.frustumCulled = false; // the shader moves them past the instanceMatrix bounds
    this.fish.castShadow = false;
    this.fish.receiveShadow = false;
    fishMat.onBeforeCompile = (shader) => {
      shader.uniforms.iTime = this.uTime;
      // A slow looping swim: drift in a gentle circle within the cell, bob up/down, with a small
      // tail wiggle. fishOrigin is the rest spot (seabed-relative); the seabed is folded into the
      // instanceMatrix translation, so we add the circling drift + bob in object/world XZ here.
      shader.vertexShader =
        'attribute vec4 fishSeed;\nattribute vec3 fishOrigin;\nattribute float tail;\nuniform float iTime;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          float ph = fishSeed.x * 6.2831853;       // start phase around the circle
          float spd = 0.25 + fishSeed.y * 0.35;    // angular speed (slow loop)
          float rad = ${WATER.lifeCell.toFixed(1)} * (0.12 + fishSeed.z * 0.18); // circle radius within the cell
          float ang = ph + iTime * spd;
          // Tail wiggle: faster than the drift, only on tail verts.
          float wig = sin(iTime * 6.0 + ph) * 0.22 * tail;
          transformed.x += wig;
          // Face the direction of travel: rotate the body about Y to the tangent of the circle.
          float fa = ang + 1.5707963; // tangent = heading
          float cf = cos(fa), sf = sin(fa);
          transformed.xz = mat2(cf, -sf, sf, cf) * transformed.xz;
          // Drift on the circle + a slow vertical bob, added to the per-instance rest origin.
          float bob = sin(iTime * 0.8 + ph) * (0.4 + fishSeed.w * 0.6);
          transformed.x += fishOrigin.x + cos(ang) * rad;
          transformed.z += fishOrigin.z + sin(ang) * rad;
          transformed.y += fishOrigin.y + bob;`,
        );
    };
    fishMat.customProgramCacheKey = () => 'oxide-fish';
    this.group.add(this.fish);

    // --- KELP ---
    const kelpMat = new THREE.MeshLambertMaterial({ color: 0x2f5d3a, side: THREE.DoubleSide });
    this.kelpSeed = new Float32Array(kelpCount * 2);
    const kelpSeedAttr = new THREE.InstancedBufferAttribute(this.kelpSeed, 2);
    kelpSeedAttr.setUsage(THREE.DynamicDrawUsage);
    const kelpGeom = makeKelpGeometry();
    kelpGeom.setAttribute('kelpSeed', kelpSeedAttr);
    this.kelp = new THREE.InstancedMesh(kelpGeom, kelpMat, kelpCount);
    this.kelp.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.kelp.frustumCulled = false;
    this.kelp.castShadow = false;
    this.kelp.receiveShadow = false;
    kelpMat.onBeforeCompile = (shader) => {
      shader.uniforms.iTime = this.uTime;
      // Slow underwater sway: bend the blade sideways, tip moving most (the 'sway' weight), like
      // grass wind but slower. Position.y is 0..1 of the instance's height, so the tip leads.
      shader.vertexShader =
        'attribute vec2 kelpSeed;\nattribute float sway;\nuniform float iTime;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          float kph = kelpSeed.x * 6.2831853;
          float amp = sway * (0.7 + kelpSeed.y * 0.5); // tip sways most; per-blade variation
          float wave = sin(iTime * 0.6 + kph) + 0.3 * sin(iTime * 1.1 + kph * 1.7);
          transformed.x += wave * amp;
          transformed.z += cos(iTime * 0.5 + kph) * amp * 0.5;`,
        );
    };
    kelpMat.customProgramCacheKey = () => 'oxide-kelp';
    this.group.add(this.kelp);

    // --- CORAL --- rigid branching clusters on the seabed; instanceColor gives each a reef tone. No
    // sway shader (coral does not bend); the animated water above it provides all the motion.
    const coralMat = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
    const coralGeom = makeCoralGeometry();
    this.coral = new THREE.InstancedMesh(coralGeom, coralMat, coralCount);
    this.coral.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.coral.frustumCulled = false;
    this.coral.castShadow = false;
    this.coral.receiveShadow = false;
    this.group.add(this.coral);

    // Carve the instance ranges into follow-grid slots (FISH fish + KELP kelp + CORAL coral each).
    for (let i = 0; i < CELLS; i++) {
      this.cells.push({ fishBase: i * FISH, kelpBase: i * KELP, coralBase: i * CORAL, cellX: NaN, cellZ: NaN, populated: false, culled: false });
    }
  }

  // Recenter the spawn grid on the focus, rebuilding only cells that changed slot, then distance-cull
  // cells past lifeCullRadius (the whole-mesh visible flag is too coarse with one InstancedMesh, so a
  // culled cell's instances collapse to zero scale -> draws nothing). Cheap: GRID^2 (25) cells, no
  // per-frame allocation. Cull tracks intra-cell movement, so the recenter check does not early-out.
  update(focusX: number, focusZ: number): void {
    const cx = Math.floor(focusX / CELL);
    const cz = Math.floor(focusZ / CELL);
    let dirty = false;

    if (cx !== this.curX || cz !== this.curZ) {
      this.curX = cx;
      this.curZ = cz;
      const H = (GRID - 1) / 2;
      for (let ax = cx - H; ax <= cx + H; ax++) {
        for (let az = cz - H; az <= cz + H; az++) {
          const sx = ((ax % GRID) + GRID) % GRID;
          const sz = ((az % GRID) + GRID) % GRID;
          const cell = this.cells[sx * GRID + sz];
          if (cell.cellX === ax && cell.cellZ === az) continue;
          cell.cellX = ax;
          cell.cellZ = az;
          this.fillCell(cell);
          dirty = true;
        }
      }
    }

    // Distance cull: hide cells whose center is beyond the cull radius; restore those that re-enter.
    const r2 = WATER.lifeCullRadius * WATER.lifeCullRadius;
    for (const cell of this.cells) {
      if (!Number.isFinite(cell.cellX)) continue;
      const dx = (cell.cellX + 0.5) * CELL - focusX;
      const dz = (cell.cellZ + 0.5) * CELL - focusZ;
      const culled = dx * dx + dz * dz > r2;
      if (culled === cell.culled) continue; // visibility unchanged: nothing to rewrite
      cell.culled = culled;
      this.applyCell(cell);
      dirty = true;
    }

    // Performance: if no nearby cell has live instances (the player is nowhere near deep water), skip
    // drawing the reef entirely. When near water, only the ~cullRadius cells around the focus hold any.
    let anyActive = false;
    for (const cell of this.cells) { if (cell.populated && !cell.culled) { anyActive = true; break; } }
    this.fish.visible = anyActive;
    this.kelp.visible = anyActive;
    this.coral.visible = anyActive;

    if (dirty) this.flush();
  }

  // Re-lay every live cell from the current terrainHeight (so the reef re-seats on the new seabed
  // after the admin panel changes the land generator), then push all the buffers.
  rebuild(): void {
    for (const cell of this.cells) {
      if (Number.isFinite(cell.cellX)) this.fillCell(cell);
    }
    this.flush();
  }

  // Mark every instanced buffer dirty so the GPU re-reads the matrices / attributes / colours.
  private flush(): void {
    this.fish.instanceMatrix.needsUpdate = true;
    if (this.fish.instanceColor) this.fish.instanceColor.needsUpdate = true;
    (this.fish.geometry.getAttribute('fishSeed') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.fish.geometry.getAttribute('fishOrigin') as THREE.InstancedBufferAttribute).needsUpdate = true;
    this.kelp.instanceMatrix.needsUpdate = true;
    (this.kelp.geometry.getAttribute('kelpSeed') as THREE.InstancedBufferAttribute).needsUpdate = true;
    this.coral.instanceMatrix.needsUpdate = true;
    if (this.coral.instanceColor) this.coral.instanceColor.needsUpdate = true;
  }

  // Lay out one cell's fish + kelp deterministically for its world coords, caching each instance's
  // transform into the live arrays. The cell is populated only if the seabed at its center is
  // underwater by minDepth. Seeds + colors are written here too (they never change with cull). Same
  // cell coords -> same layout, so recenter never reshuffles. applyCell then pushes the matrices.
  private fillCell(cell: Cell): void {
    const x0 = cell.cellX * CELL;
    const z0 = cell.cellZ * CELL;
    const centerSeabed = terrainHeight(x0 + CELL * 0.5, z0 + CELL * 0.5);
    cell.populated = centerSeabed < DEEP_Y;
    const rng = mulberry32(cellSeed(cell.cellX, cell.cellZ));

    // FISH: a small school, each circling within the cell between the seabed and just below surface.
    for (let f = 0; f < FISH; f++) {
      const idx = cell.fishBase + f;
      const o4 = idx * 4, o3 = idx * 3;
      if (!cell.populated) {
        this.zeroMat(this.fishLive, idx);
        continue;
      }
      const rx = rng(), rz = rng();
      const px = x0 + (0.2 + rx * 0.6) * CELL;
      const pz = z0 + (0.2 + rz * 0.6) * CELL;
      const seabed = terrainHeight(px, pz);
      if (seabed > SHORE_Y) { this.zeroMat(this.fishLive, idx); continue; } // dry spot: no fish here
      // Rest height: a band between ~0.6m off the seabed and ~1m below the surface, clamped.
      const lo = seabed + 0.6;
      const hi = Math.max(lo + 0.5, FISH_CEIL);
      const restY = lo + rng() * (hi - lo);
      // The matrix carries the world rest position; the shader adds the circle + bob (centered on
      // that position via the zeroed fishOrigin), so the school drifts around its seeded home.
      this.tmpPos.set(px, restY, pz);
      this.tmpQuat.identity();
      const s = 0.7 + rng() * 0.6; // size variation
      this.tmpScale.set(s, s, s);
      this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.tmp.toArray(this.fishLive, idx * 16);
      // Per-instance swim seed: phase, angular-speed, circle-radius, bob-amount.
      this.fishSeed[o4] = rng();
      this.fishSeed[o4 + 1] = rng();
      this.fishSeed[o4 + 2] = rng();
      this.fishSeed[o4 + 3] = rng();
      this.fishOrigin[o3] = 0;
      this.fishOrigin[o3 + 1] = 0;
      this.fishOrigin[o3 + 2] = 0;
      // Two muted fish tones (steel-blue and dusky tan), picked per instance.
      if (rng() < 0.5) this.tmpCol.setRGB(0.42, 0.52, 0.6);
      else this.tmpCol.setRGB(0.6, 0.52, 0.4);
      this.fish.setColorAt(idx, this.tmpCol);
    }

    // KELP: rooted on the seabed, stretched up toward the surface, swaying in the shader.
    for (let k = 0; k < KELP; k++) {
      const idx = cell.kelpBase + k;
      const o2 = idx * 2;
      if (!cell.populated) {
        this.zeroMat(this.kelpLive, idx);
        continue;
      }
      const rx = rng(), rz = rng();
      const px = x0 + (0.1 + rx * 0.8) * CELL;
      const pz = z0 + (0.1 + rz * 0.8) * CELL;
      const seabed = terrainHeight(px, pz);
      if (seabed > SHORE_Y) { this.zeroMat(this.kelpLive, idx); continue; } // dry spot: no kelp here
      // Top reaches toward, but not through, the surface; height = (target - seabed), clamped.
      const top = Math.min(TERRAIN.seaLevel - 0.4, seabed + WATER.minDepth + 4.0);
      const height = Math.max(1.2, top - seabed);
      const yaw = rng() * Math.PI * 2;
      this.tmpPos.set(px, seabed, pz);
      this.tmpQuat.setFromAxisAngle(UP, yaw);
      const w = 0.7 + rng() * 0.7; // width variation
      this.tmpScale.set(w, height, w);
      this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.tmp.toArray(this.kelpLive, idx * 16);
      this.kelpSeed[o2] = rng();     // sway phase
      this.kelpSeed[o2 + 1] = rng(); // per-blade sway amount
    }

    // CORAL: rigid clusters seated on the seabed, each a random reef tone via instanceColor.
    for (let c = 0; c < CORAL; c++) {
      const idx = cell.coralBase + c;
      if (!cell.populated) {
        this.zeroMat(this.coralLive, idx);
        continue;
      }
      const rx = rng(), rz = rng();
      const px = x0 + (0.1 + rx * 0.8) * CELL;
      const pz = z0 + (0.1 + rz * 0.8) * CELL;
      const seabed = terrainHeight(px, pz);
      if (seabed > SHORE_Y) { this.zeroMat(this.coralLive, idx); continue; } // dry spot: no coral here
      const yaw = rng() * Math.PI * 2;
      this.tmpPos.set(px, seabed, pz);
      this.tmpQuat.setFromAxisAngle(UP, yaw);
      const s = 0.7 + rng() * 1.1; // cluster size variation
      this.tmpScale.set(s, s * (0.8 + rng() * 0.5), s);
      this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.tmp.toArray(this.coralLive, idx * 16);
      // Reef palette: pink, orange, violet, teal.
      const t = rng();
      if (t < 0.3) this.tmpCol.setRGB(0.95, 0.42, 0.55);
      else if (t < 0.6) this.tmpCol.setRGB(0.96, 0.55, 0.28);
      else if (t < 0.8) this.tmpCol.setRGB(0.62, 0.4, 0.78);
      else this.tmpCol.setRGB(0.3, 0.72, 0.66);
      this.coral.setColorAt(idx, this.tmpCol);
    }

    this.applyCell(cell);
  }

  // Push this cell's instance matrices: the cached live transform when populated AND on-disc, else a
  // zero matrix so it draws nothing. Allocation-free (writes straight into the instanceMatrix array).
  private applyCell(cell: Cell): void {
    const draw = cell.populated && !cell.culled;
    const fdst = this.fish.instanceMatrix.array as Float32Array;
    for (let f = 0; f < FISH; f++) {
      const idx = cell.fishBase + f;
      if (draw) fdst.set(this.fishLive.subarray(idx * 16, idx * 16 + 16), idx * 16);
      else this.zeroMat(fdst, idx);
    }
    const kdst = this.kelp.instanceMatrix.array as Float32Array;
    for (let k = 0; k < KELP; k++) {
      const idx = cell.kelpBase + k;
      if (draw) kdst.set(this.kelpLive.subarray(idx * 16, idx * 16 + 16), idx * 16);
      else this.zeroMat(kdst, idx);
    }
    const cdst = this.coral.instanceMatrix.array as Float32Array;
    for (let c = 0; c < CORAL; c++) {
      const idx = cell.coralBase + c;
      if (draw) cdst.set(this.coralLive.subarray(idx * 16, idx * 16 + 16), idx * 16);
      else this.zeroMat(cdst, idx);
    }
  }

  // Store a zero-scale matrix into a 16-float-per-instance backing array (collapses to nothing).
  private zeroMat(arr: Float32Array, idx: number): void {
    const o = idx * 16;
    for (let i = 0; i < 16; i++) arr[o + i] = 0;
    arr[o + 15] = 1; // keep w so it is a valid (degenerate) transform
  }
}
